import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { AnimatePresence, motion } from "framer-motion";
import "./Navigation.css";
/* Cleaned lockup: the old CafevistaWhite.png was 7639x2836 (~83 MB decoded)
   rendered at 42px, and had the tagline baked in at ~4px tall. Swap this
   import for cafevista-lockup-cyan.png to use the cyan-icon variant. */
import logo from "../../assets/logos/cafevista-lockup.png";
import collabizLogo from "../../assets/logos/collabiz.svg";
import Icon from "../common/Icon";
import { products, serviceCategories, solutionPillars } from "../../Data/siteContent";

const Chevron = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);


export default function NavigationBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("auth-token");
    if (token) {
      try {
        setUser(jwtDecode(token));
      } catch (e) {
        /* invalid token */
      }
    }
  }, []);

  const logout = () => {
    localStorage.removeItem("auth-token");
    setUser(null);
    navigate("/login");
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menus on navigation
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  // Lock scroll while the mobile overlay is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const productsPanel = (
    <div className="cv-nav__drop cv-nav__drop--ventures">
      {products.map((p) => (
        <Link key={p.id} className="cv-nav__vitem" to={`/products#${p.id}`}>
          <span className={`cv-nav__vic cv-acc-${p.accent}`}>
            {p.logo ? <img src={collabizLogo} alt="" /> : <Icon name={p.icon} size={20} />}
          </span>
          <span className="cv-nav__vtext">
            <strong>
              {p.name}
              {p.status === "live" && <em className="cv-nav__badge">LIVE</em>}
            </strong>
            <span>{p.kicker}</span>
          </span>
        </Link>
      ))}
      <Link className="cv-nav__sall" to="/products#digital">
        Digital products <Icon name="arrow" size={15} />
      </Link>
    </div>
  );

  const solutionsPanel = (
    <div className="cv-nav__drop cv-nav__drop--solutions">
      <div className="cv-nav__pillars">
        {solutionPillars.map((p) => (
          <Link key={p.id} className="cv-nav__pillar" to={`/service#${p.id}`}>
            <span className={`cv-nav__sic cv-acc-${p.accent}`}><Icon name={p.icon} size={17} /></span>
            <span className="cv-nav__ptext">
              <strong>{p.name}</strong>
              <span>{p.kicker}</span>
            </span>
          </Link>
        ))}
      </div>

      <span className="cv-nav__sdivider">Delivery domains</span>
      <div className="cv-nav__sgrid">
        {serviceCategories.map((s) => (
          <Link key={s.id} className="cv-nav__sitem" to={`/service#${s.id}`}>
            <span className={`cv-nav__sic cv-acc-${s.accent}`}><Icon name={s.icon} size={16} /></span>
            {s.name}
          </Link>
        ))}
      </div>
      <Link to="/service" className="cv-nav__sall">
        View all solutions <Icon name="arrow" size={15} />
      </Link>
    </div>
  );

  return (
    <header className={`cv-nav ${scrolled ? "is-scrolled" : ""} ${menuOpen ? "is-menu-open" : ""}`}>
      <div className="cv-nav__inner">
        <Link to="/home" className="cv-nav__brand" aria-label="CafeVista home">
          <img src={logo} alt="CafeVista" />
        </Link>

        {/* Desktop links */}
        <nav className="cv-nav__links" aria-label="Primary">
          <NavLink to="/home" className={({ isActive }) => `cv-nav__link ${isActive ? "is-active" : ""}`}>
            Home
          </NavLink>
          <NavLink to="/about" className={({ isActive }) => `cv-nav__link ${isActive ? "is-active" : ""}`}>
            About
          </NavLink>

          <div className="cv-nav__has-drop">
            <button className="cv-nav__link cv-nav__drop-btn" aria-haspopup="true">
              Products <Chevron />
            </button>
            {productsPanel}
          </div>

          <div className="cv-nav__has-drop">
            <button className="cv-nav__link cv-nav__drop-btn" aria-haspopup="true">
              Solutions <Chevron />
            </button>
            {solutionsPanel}
          </div>

          <NavLink to="/contactus" className={({ isActive }) => `cv-nav__link ${isActive ? "is-active" : ""}`}>
            Contact Us
          </NavLink>
        </nav>

        <div className="cv-nav__right">
          {user ? (
            <button onClick={logout} className="cv-btn cv-btn--sm">Logout</button>
          ) : (
            <Link to="/contactus" className="cv-btn cv-btn--sm cv-nav__cta">Start a Project</Link>
          )}
          <button
            className={`cv-nav__burger ${menuOpen ? "is-open" : ""}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      {/* Mobile full-screen overlay.
          Rendered into <body> on purpose: .cv-nav uses backdrop-filter, which
          makes the header a containing block for position:fixed descendants.
          Left inside it, "inset: 0" resolved against the 72px header instead of
          the viewport, so the overlay was 390x72 and the menu was centred at
          top:-321px - mostly off-screen. */}
      {createPortal(
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="cv-nav__overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <motion.nav
              className="cv-nav__mobile"
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } } }}
            >
              {[
                { label: "Home", to: "/home" },
                { label: "About", to: "/about" },
                { label: "Products", to: "/products" },
                { label: "Solutions", to: "/service" },
                { label: "Contact Us", to: "/contactus" },
              ].map((l) => (
                <motion.div
                  key={l.label}
                  variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } }}
                >
                  <Link to={l.to} className="cv-nav__mlink" onClick={() => setMenuOpen(false)}>
                    {l.label}
                  </Link>
                </motion.div>
              ))}

              <motion.div
                className="cv-nav__mventures"
                variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } }}
              >
                <span className="cv-nav__mlabel">Our Products</span>
                {products.map((p) => (
                  <Link key={p.id} to={`/products#${p.id}`} className="cv-nav__mventure" onClick={() => setMenuOpen(false)}>
                    {p.name}
                  </Link>
                ))}
              </motion.div>

              <motion.div variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } }}>
                {user ? (
                  <button onClick={() => { logout(); setMenuOpen(false); }} className="cv-btn cv-nav__mcta">Logout</button>
                ) : (
                  <Link to="/contactus" className="cv-btn cv-nav__mcta" onClick={() => setMenuOpen(false)}>Start a Project</Link>
                )}
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
      )}
    </header>
  );
}
